/**
 * LayoutDesignerTab - Visual menu/category arrangement tool
 * Drag and drop to reorder categories and menu items
 */
import { useState, useEffect } from 'react';
import { 
  GripVertical, Save, RotateCcw, Eye, Layout, 
  ChevronRight, ChevronDown, Coffee, Image, Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function LayoutDesignerTab({ authHeaders }) {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [categoryImages, setCategoryImages] = useState({});

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [menuRes, catImgRes] = await Promise.all([
        api.get('/menu'),
        api.get('/categories/images')
      ]);
      
      const items = menuRes.data;
      setMenuItems(items);
      
      // Extract unique categories with display order
      const cats = [...new Set(items.map(i => i.category))];
      setCategories(cats.map((cat, idx) => ({
        id: cat,
        name: cat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        order: idx,
        itemCount: items.filter(i => i.category === cat).length
      })));
      
      setCategoryImages(catImgRes.data.images || {});
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load layout data');
    }
    setLoading(false);
  };

  const handleCategoryDragStart = (e, category) => {
    setDraggedCategory(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragOver = (e, targetCategory) => {
    e.preventDefault();
    if (!draggedCategory || draggedCategory.id === targetCategory.id) return;
    
    const newCategories = [...categories];
    const draggedIdx = newCategories.findIndex(c => c.id === draggedCategory.id);
    const targetIdx = newCategories.findIndex(c => c.id === targetCategory.id);
    
    newCategories.splice(draggedIdx, 1);
    newCategories.splice(targetIdx, 0, draggedCategory);
    
    setCategories(newCategories.map((c, idx) => ({ ...c, order: idx })));
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategory(null);
  };

  const handleItemDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) return;
    if (draggedItem.category !== targetItem.category) return;
    
    const newItems = [...menuItems];
    const draggedIdx = newItems.findIndex(i => i.id === draggedItem.id);
    const targetIdx = newItems.findIndex(i => i.id === targetItem.id);
    
    newItems.splice(draggedIdx, 1);
    newItems.splice(targetIdx, 0, draggedItem);
    
    setMenuItems(newItems);
  };

  const handleItemDragEnd = () => {
    setDraggedItem(null);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      // Save category order
      const categoryOrder = categories.map((c, idx) => ({
        category: c.id,
        display_order: idx
      }));

      // Save item order within categories
      const itemOrder = menuItems.map((item, idx) => ({
        id: item.id,
        display_order: idx
      }));
      
      await api.post('/admin/layout', {
        category_order: categoryOrder,
        item_order: itemOrder
      });

      toast.success('Layout saved successfully');
    } catch (err) {
      console.error('Layout save error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save layout');
    }
    setSaving(false);
  };

  const getCategoryItems = (categoryId) => {
    return menuItems.filter(item => item.category === categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="layout-designer-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Layout className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Layout Designer</h2>
            <p className="text-muted-foreground text-sm">Drag and drop to arrange categories and items</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="rounded-full">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button onClick={saveLayout} disabled={saving} className="rounded-full">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Layout'}
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          <strong>How to use:</strong> Drag categories to reorder them. Click a category to expand and reorder items within it.
          Changes will be reflected on the menu page after saving.
        </p>
      </div>

      {/* Category List */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="p-4 bg-muted/30 border-b border-border">
          <h3 className="font-heading text-lg text-foreground">Categories</h3>
        </div>
        <div className="divide-y divide-border">
          {categories.map((category, idx) => (
            <div key={category.id}>
              {/* Category Row */}
              <div
                draggable
                onDragStart={(e) => handleCategoryDragStart(e, category)}
                onDragOver={(e) => handleCategoryDragOver(e, category)}
                onDragEnd={handleCategoryDragEnd}
                className={`flex items-center gap-4 p-4 cursor-move transition-colors ${
                  draggedCategory?.id === category.id ? 'bg-primary/10' : 'hover:bg-muted/50'
                }`}
              >
                <GripVertical className="w-5 h-5 text-muted-foreground" />
                
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                  {categoryImages[category.id] ? (
                    <img 
                      src={categoryImages[category.id]} 
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Coffee className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="font-medium text-foreground">{category.name}</div>
                  <div className="text-sm text-muted-foreground">{category.itemCount} items</div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-muted rounded-full text-xs text-muted-foreground">
                    Position {idx + 1}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                  >
                    {expandedCategory === category.id ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Expanded Items */}
              {expandedCategory === category.id && (
                <div className="bg-muted/20 px-4 py-2">
                  <div className="pl-14 space-y-2">
                    {getCategoryItems(category.id).map((item, itemIdx) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleItemDragStart(e, item)}
                        onDragOver={(e) => handleItemDragOver(e, item)}
                        onDragEnd={handleItemDragEnd}
                        className={`flex items-center gap-3 p-3 bg-card rounded-lg border border-border cursor-move transition-colors ${
                          draggedItem?.id === item.id ? 'bg-primary/10' : 'hover:bg-muted/50'
                        }`}
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground text-sm truncate">{item.name}</div>
                          <div className="text-xs text-muted-foreground">
                            ${item.price?.toFixed(2) || item.sizes?.[0]?.price?.toFixed(2)}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {item.is_bestseller && (
                            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded-full">
                              Bestseller
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">#{itemIdx + 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Preview Link */}
      <div className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-2xl border border-violet-200 dark:border-violet-800 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg text-foreground">Preview Changes</h3>
            <p className="text-sm text-muted-foreground">See how your layout will look on the menu page</p>
          </div>
          <Button
            onClick={() => window.open('/menu', '_blank')}
            variant="outline"
            className="rounded-full"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Menu
          </Button>
        </div>
      </div>
    </div>
  );
}
